import React from 'react';
import { connect } from 'react-redux';
import { isNil, get, isEmpty } from 'lodash';

import { Overlay } from 'src/common-ui/index';
import CompanionListView, {
  CollapsibleCompanion,
  Props as CompanionProps,
} from 'src/common-ui/components/CompanionListView/CompanionListView';

import { listPairStyle, companionStyles } from 'src/components/ConfigurableGrid/ConfigurableGrid.styles';
import Subheader from 'src/components/Subheader/Subheader.container';
import { OvertimeView } from 'src/pages/AssortmentBuild/OvertimeView/OvertimeView';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { ASSORTMENT, ASSORTMENT_BUILD_FILTER_ALL_WARNING } from 'src/utils/Domain/Constants';
import container from 'src/ServiceContainer';
import { AppState, AppThunkDispatch } from 'src/store';
import { getHeaderTextFromState } from 'src/components/ExtendedDataGrid/ExtendedDataGrid';
import { resolvePath } from 'src/cdn';

import noImagePath from 'src/common-ui/images/noimage.jpg';
import { CompanionDataLookup, parseCompanionListViewConfig } from 'src/utils/Component/ListView';
import { companionDataParse } from 'src/components/ListGridPair/ListGridPair.utils';
import { FavoriteListItemStorage, FavoriteResponseItem } from 'src/components/Subheader/Favorites/Favorites.types';
import { SubheaderSlice } from 'src/components/Subheader/Subheader.slice';
import { getLocalConfig } from 'src/components/ViewConfiguratorModal/ViewConfiguratorModal.utils';
import { isViewDefnLoaded, TenantConfigViewData } from 'src/dao/tenantConfigClient';
import {
  requestTenantConfig,
  receiveTenantConfig,
  setSelectedId,
  updateTenantConfig,
} from 'src/pages/AssortmentBuild/OvertimeView/StyleByLevel/StyleByLevelOvertime.slice';
import { updateCompanionSortField, updateCompanionSortDirection } from 'src/components/Subheader/Subheader.slice';
import { isDataLoaded } from 'src/services/pivotServiceCache';
import { Option } from 'src/components/Configure/ConfigureModal';
import { ViewConfiguratorModalProps } from 'src/components/ViewConfiguratorModal/ViewConfiguratorModal';
import { SortByDirection } from 'src/components/Subheader/Subheader.types';
import { ViewDataState } from 'src/types/Domain';
import { bindActionCreators } from '@reduxjs/toolkit';
import { toggleCompanionCollapse } from 'src/state/uipose/UiPose.slice';
import { NestedOvertimeOwnProps } from 'configuration-schemas/ownProps';
import { zNestedStyleOvertimeViewDefn, NestedStyleOvertimeViewDefn } from 'configuration-schemas/viewdefns/viewdefn';

const noImage = resolvePath(noImagePath);

export interface StyleByLevelOvertimeOwnProps extends NestedOvertimeOwnProps {}

export interface StyleByLevelOvertimeDispatchProps extends Pick<CollapsibleCompanion, 'onToggleCompanion'> {
  onShowView: () => void;
  onRefresh: (id: string) => void;
  onConfigUpdate: (config: any) => void;
  updateCompanionSortField: (field: string) => void;
  updateCompanionSortDirection: (dir: SortByDirection) => void;
}

interface StyleByLevelOvertimeValueProps extends Pick<CollapsibleCompanion, 'isCompanionCollapsed'> {
  customHeader: string;
  subheaderState: SubheaderSlice;
  aggBys: string[];
  configLoaded: boolean;
  dataLoaded: boolean;
  data?: BasicPivotItem[];
  companionData: BasicPivotItem[];
  viewDefn: NestedStyleOvertimeViewDefn;
  unmodifiedViewDefn?: NestedStyleOvertimeViewDefn;
  companionDefn?: CompanionDataLookup;
  selectedCompanionId: string;
  configureSelections?: Option[];
  defaultConfigureSelections?: Option[];
  viewDataState: ViewDataState;
  companionSortField: string | null;
  companionSortDirection: SortByDirection;
}

export interface StyleByLevelOvertimeProps
  extends StyleByLevelOvertimeOwnProps,
    StyleByLevelOvertimeValueProps,
    StyleByLevelOvertimeDispatchProps {}

interface State {
  configureIsOpen: boolean;
}

function dispatchToProps(
  dispatch: AppThunkDispatch,
  { defns }: StyleByLevelOvertimeOwnProps,
): StyleByLevelOvertimeDispatchProps {
  return {
    ...bindActionCreators(
      {
        onToggleCompanion: toggleCompanionCollapse,
        updateCompanionSortField,
        updateCompanionSortDirection,
      },
      dispatch,
    ),
    async onShowView() {
      dispatch(requestTenantConfig());
      const configResponse =
        await container.tenantConfigClient.getTenantViewDefnsWithFavorites<NestedStyleOvertimeViewDefn>({
          defnIds: [defns.view[0]],
          appName: ASSORTMENT,
          validationSchemas: [zNestedStyleOvertimeViewDefn],
        });
      const localConfig: FavoriteListItemStorage | undefined = getLocalConfig(
        defns.view[0],
        configResponse[1] as unknown as FavoriteResponseItem[],
        dispatch,
        configResponse[0] as unknown as TenantConfigViewData,
      );
      const configWithFavorites = isNil(localConfig)
        ? configResponse[0]
        : (localConfig.config as unknown as NestedStyleOvertimeViewDefn);

      const [, ...moreDefns] = defns.view;
      const moreConfigResponses = await container.tenantConfigClient.getTenantViewDefnsWithFavorites({
        defnIds: moreDefns,
        appName: ASSORTMENT,
      });
      dispatch(
        receiveTenantConfig({
          viewDefn: configWithFavorites,
          unmodifiedViewDefn: configResponse[0],
          companionDefn: parseCompanionListViewConfig(moreConfigResponses[0]),
        }),
      );
    },
    onRefresh(selectedId: string) {
      dispatch(setSelectedId(selectedId));
    },
    onConfigUpdate(config: any) {
      dispatch(updateTenantConfig(config as NestedStyleOvertimeViewDefn));
    },
  };
}
function mapStateToProps(state: AppState, ownProps: StyleByLevelOvertimeOwnProps): StyleByLevelOvertimeValueProps {
  const customHeader = getHeaderTextFromState(state, true);
  const viewState = state.pages.hindsighting.styleByLevelOvertime;
  const {
    viewDataState,
    companionDataState,
    viewDefnState,
    data,
    companionData,
    viewDefn,
    unmodifiedViewDefn,
    companionDefn,
    selectedCompanionId,
    configureSelections,
    defaultConfigureSelections,
  } = viewState;
  const configLoaded = isViewDefnLoaded(viewDefnState) && !isEmpty(viewDefn);
  const dataLoaded = isDataLoaded(viewDataState) && isDataLoaded(companionDataState);

  return {
    ...ownProps,
    configLoaded,
    dataLoaded,
    customHeader,
    subheaderState: state.subheader,
    data,
    companionData,
    viewDefn,
    companionDefn,
    unmodifiedViewDefn,
    selectedCompanionId,
    configureSelections,
    defaultConfigureSelections,
    // FIXME: move me to ownProps dataApi or something
    aggBys: ['style', 'stylecolor'],
    viewDataState,
    isCompanionCollapsed: state.uiPose.isCompanionCollapsed,
    companionSortField: state.subheader.companionSortField,
    companionSortDirection: state.subheader.companionSortDirection,
  };
}

class StyleByLevelOvertime extends React.Component<StyleByLevelOvertimeProps, State> {
  constructor(props: StyleByLevelOvertimeProps) {
    super(props);
    this.state = {
      configureIsOpen: false,
    };
  }

  componentDidMount() {
    this.props.onShowView();
  }

  renderCompanionView = () => {
    const {
      viewDefn,
      companionDefn,
      companionData,
      selectedCompanionId,
      isCompanionCollapsed,
      onToggleCompanion,
      companionSortDirection,
      companionSortField,
    } = this.props;

    if (isNil(companionData) || isNil(companionDefn) || isNil(viewDefn) || isNil(viewDefn.companionGrid)) {
      return null;
    }

    const { options: sortOptions, defaultSelection } = viewDefn.companionGrid.sortConfig;
    const sortBy =
      !isNil(companionSortField) && sortOptions.some((opt) => opt.dataIndex === companionSortField)
        ? companionSortField
        : defaultSelection;

    const data = companionDataParse(companionData, companionDefn, companionSortDirection, sortBy).filter(
      (i) => i.title !== '' && i.title != null,
    );
    const sortSelection = sortOptions.findIndex((option) => option.dataIndex === sortBy);
    const defaultSelectionIndex = sortOptions.findIndex((option) => option.dataIndex === defaultSelection);
    const currentSelectionInd = data.findIndex((option) => option.id === selectedCompanionId);
    const selectedIndex = currentSelectionInd >= 0 ? currentSelectionInd : 0;

    const companionProps: CompanionProps = {
      isCompanionCollapsed,
      defaultSelection: sortSelection > -1 ? sortSelection : defaultSelectionIndex,
      initialSortDirection: companionSortDirection,
      selectedIndex,
      sortOptions,
      isDataloaded: this.props.dataLoaded,
      label: viewDefn.companionGrid.label,
      data,
      className: companionStyles,
      dataLookup: companionDefn,
      onListItemClicked: (classId: string) => {
        if (selectedCompanionId === classId) {
          return;
        }
        this.props.onRefresh(classId);
      },
      onChangeDirection: (direction) => {
        if (direction === companionSortDirection) {
          return;
        }
        this.props.updateCompanionSortDirection(direction);
      },
      onSortSelection: (sortOption) => {
        if (sortOption.dataIndex === sortBy) {
          return;
        }
        this.props.updateCompanionSortField(sortOption.dataIndex);
      },
      onToggleCompanion,
      noImageUrl: noImage,
    };

    return <CompanionListView {...companionProps} />;
  };

  onConfigUpdateLocal = (config: any) => {
    if (config.isDefault) {
      this.props.updateCompanionSortField(this.props.viewDefn.companionGrid?.sortConfig.defaultSelection ?? '');
      this.props.updateCompanionSortDirection('desc');
    }
    this.props.onConfigUpdate(config);
  };

  render() {
    if (!this.props.configLoaded) {
      return <Overlay visible={true} type={'loading'} qaKey={'overtime-loading'} />;
    }

    const {
      title = 'Style Color Over Time',
      viewDefn,
      unmodifiedViewDefn,
      subheaderState,
      data,
      dataLoaded,
      aggBys,
      customHeader,
      configureSelections,
      defaultConfigureSelections,
      viewDataState,
      isCompanionCollapsed,
      companionSortField,
      companionSortDirection,
    } = this.props;

    const defaultSortField = viewDefn.companionGrid?.sortConfig.defaultSelection ?? '';
    const sortBy = companionSortField ?? defaultSortField;

    const viewConfigurator: ViewConfiguratorModalProps = {
      viewConfig: viewDefn as unknown as TenantConfigViewData,
      unmodifiedViewDefn: unmodifiedViewDefn as unknown as TenantConfigViewData,
      companionData: {
        companionSortDirection,
        companionSortField: sortBy,
      },
      defaultCompanionData: {
        companionSortDirection: 'desc',
        companionSortField: defaultSortField,
      },
      configureSelections,
      defaultConfigureSelections,
      updateConfig: this.onConfigUpdateLocal,
    };

    const filteredViewDefnCols = viewDefn.columns?.filter((col) => isNil(col.visible) || col.visible);
    const finalViewDefn = { ...viewDefn, columns: filteredViewDefnCols };

    return (
      <div className={listPairStyle}>
        <Subheader
          viewConfigurator={viewConfigurator}
          title={title}
          showSearch={true}
          showFlowStatus={false}
          errorCondition={ASSORTMENT_BUILD_FILTER_ALL_WARNING}
          viewDataState={viewDataState}
        />
        <div className={'data-container'}>
          {this.renderCompanionView()}
          <div className="over-time-container">
            <OvertimeView
              loaded={dataLoaded}
              viewConfig={finalViewDefn}
              rowHeight={get(finalViewDefn, 'main.rowHeight', 30)}
              aggBys={aggBys}
              treeData={data || []}
              search={subheaderState.search}
              companionOpen={!isCompanionCollapsed}
              hasCompanion={true}
              exportOptions={{
                fileName: title,
                customHeader: customHeader,
              }}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default connect(mapStateToProps, dispatchToProps)(StyleByLevelOvertime);
